/* eslint-disable max-lines */
import { AuthTracking } from "@/lib/tracking/auth";
import { useCallback, useState, useRef, type FocusEvent, type ChangeEvent } from "react";
import {
  Label,
  Input,
  Checkbox,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Tooltip,
  toast,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Combobox,
  Button,
  InputPassword,
  Spinner,
  Stack,
  Textarea,
} from "@altimateai/lego";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BaseContainer } from "./components/base";
import {
  freeRegistrationSchema,
  isFreeEmailProvider,
  validateEmail,
  validateFormPassword,
} from "./helper";
import { useMutation } from "@tanstack/react-query";
import { companyAvailable, companyRegister } from "../../helpers/apis";
import { isMobile } from "@/helpers/utils";
import { cn } from "@/lib/utils";
import { debounce } from "lodash";
import { FREE_USER_DOMAIN } from "../../helpers/tenant";
import query_insights_robot from "../../assets/images/query_insights_robot.png";
import { IconCircleCheck, IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";

import MailChecker from "mailchecker";
import { PasswordValidation } from "./PasswordValidator";
import { z } from "zod";
import { REFERRAL_CODE_PATTERN } from "@altimateai/internal";
import {
  EMAIL_INPUT_ID,
  PASSWORD_INPUT_ID,
  TENANT_INPUT_ID,
  SIGNUP_SOURCE_OPTIONS,
  OTHER_SOURCE_INPUT_ID,
  REFERRAL_CODE_INPUT_ID,
  AI_TOOL_OPTIONS,
} from "./constants";

const formSchema = freeRegistrationSchema.extend({
  newsletter: z.boolean().default(true),
  source_insight: z.string().default(""),
  other_source: z.string().default(""),
  referral_code: z.string().default(""),
  ai_tools: z.array(z.string()).default([]),
  ai_tools_other: z.string().default(""),
  frustrations: z.string().default(""),
  hopes: z.string().default(""),
});

type FormValues = z.infer<typeof formSchema>;

const FreeRegister = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const isExtensionVisitor = searchParams.get("source") === "extension";

  const [validatePassword, setValidatePassword] = useState([0, 0, 0, 0, 0]);
  const [activeElement, setActiveElement] = useState<HTMLElement | null>(null);
  const [validTenant, setIsValidTenant] = useState(true);
  const defaultTenantMessage = "";
  const [tenantError, setTenantError] = useState(defaultTenantMessage);
  const [isPendingTenantCheck, setIsPendingTenantCheck] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company: "",
      email: "",
      name: "",
      password: "",
      newsletter: true,
      source_insight: "",
      other_source: "",
      referral_code: "",
      ai_tools: [],
      ai_tools_other: "",
      frustrations: "",
      hopes: "",
    },
  });

  const { mutate: registerCompany, isPending } = useMutation({
    mutationFn: companyRegister,
    onSuccess: response => {
      if (response?.ok) {
        AuthTracking.freeSignupInstanceCreated(form.getValues("source_insight") || "");
        navigate("/verified", { state: { email: form.getValues("email") } });
      }
    },
    onError: (response: unknown) => {
      if (response === "sendgrid_invalid_email") {
        form.setError("email", {
          message:
            "Email validation failed. Please double-check your address or contact us at signup@altimate.ai for assistance.",
        });
        return;
      }
      if (response === "referral_code_invalid") {
        form.setError("referral_code", {
          message:
            "This referral code is invalid, expired, or has reached its limit. Please check and try again.",
        });
        return;
      }
      if (response === "email_already_registered") {
        form.setError("email", {
          message:
            "An account already exists for this email (or a Gmail alias of it). Please sign in instead.",
        });
        return;
      }
      if (response === "free_email_not_allowed") {
        form.setError("email", {
          message:
            "Please use a business email, or enter a referral code above if you have one.",
        });
        return;
      }
      toast({
        title: "Failed to create instance",
        variant: "destructive",
      });
    },
  });

  const onFocusChange = (e: FocusEvent<HTMLInputElement>) => {
    setActiveElement(e.target);
  };

  const isTenantValid = (tenantName: string) => {
    const isValid =
      !["app", "www", "tenant", "api", "public", "postgres", "private"].includes(tenantName) &&
      /^[a-z_][a-z0-9_]*$/.test(tenantName);
    setIsValidTenant(isValid);
    return isValid;
  };

  const { mutate: isTenantAvailable, isPending: isTenantChecking } = useMutation({
    mutationFn: companyAvailable,
    onSuccess: data => {
      setIsPendingTenantCheck(false);
      if (!data?.ok) {
        setTenantError(
          "This instance name has been used already. Please check with your team if they can invite you to the instance. You can also choose another instance name."
        );
        setIsValidTenant(false);
      } else {
        setTenantError(defaultTenantMessage);
        setIsValidTenant(true);
      }
    },
    onError: (data: unknown) => {
      setIsPendingTenantCheck(false);
      setTenantError(String(data));
      setIsValidTenant(false);
    },
  });

  const debouncedFnRef = useRef(
    debounce(
      (name: string) => {
        if (name !== "") {
          if (isTenantValid(name)) {
            isTenantAvailable(name);
          } else {
            setIsPendingTenantCheck(false);
          }
        } else {
          setIsPendingTenantCheck(false);
        }
      },
      500,
      { trailing: true }
    )
  );

  const debouncedIsTenantAvailable = useCallback((name: string) => {
    setIsPendingTenantCheck(true);
    debouncedFnRef.current(name);
  }, []);

  const isFormDisabled = () => {
    const values = form.getValues();
    if (values.company === "") return true;
    if (values.email === "") return true;
    if (values.name === "") return true;

    if (form.formState.errors.email) return true;
    if (!validTenant) return true;
    if (validatePassword.includes(-1) || validatePassword.includes(0)) return true;
    if (values.source_insight === "Other" && values.other_source.trim() === "") return true;

    const referralCode = values.referral_code?.trim() ?? "";
    if (referralCode !== "") {
      if (!REFERRAL_CODE_PATTERN.test(referralCode)) return true;
      if (values.ai_tools.length === 0) return true;
      if (values.ai_tools.includes("other") && values.ai_tools_other.trim() === "") return true;
      if (values.frustrations.trim() === "") return true;
      if (values.hopes.trim() === "") return true;
    }

    return false;
  };

  const isMobileView = isMobile();

  const onChangeSelectSource = (src: string | string[]) => {
    const value = Array.isArray(src) ? src[0] : src;
    form.setValue("source_insight", value || "");
    if (value !== "Other") {
      form.setValue("other_source", "");
    }
  };

  const handleFormSubmit = async (values: FormValues) => {
    // Revalidate the form before submitting
    isTenantAvailable(values.company);
    const email = values.email;
    const hasReferral = (values.referral_code?.trim() ?? "") !== "";
    let isValidEmail = false;
    if (!email) {
      isValidEmail = true;
    } else if (isFreeEmailProvider(email, hasReferral)) {
      form.setError("email", { message: "Please use a valid business email address" });
    } else if (!MailChecker.isValid(email)) {
      form.setError("email", { message: "Please use a valid email address" });
    } else {
      isValidEmail = true;
      form.clearErrors("email");
    }
    const isValidtenant = isTenantValid(values.company) && validTenant;

    const finalSignupSource =
      values.source_insight === "Other" ? values.other_source : values.source_insight;

    const referralCode = values.referral_code?.trim() ?? "";
    if (hasReferral && !REFERRAL_CODE_PATTERN.test(referralCode)) {
      form.setError("referral_code", {
        message: "Use 4–64 letters, numbers, hyphens or underscores",
      });
      return;
    }

    const {
      referral_code: _rc,
      ai_tools,
      ai_tools_other,
      frustrations: frust,
      hopes: hp,
      other_source: _os,
      ...baseValues
    } = values;
    const submitValues = {
      ...baseValues,
      source_insight: finalSignupSource,
      ...(hasReferral
        ? {
            referral_code: referralCode,
            survey_responses: {
              ai_tools,
              ...(ai_tools.includes("other") && ai_tools_other.trim() !== ""
                ? { ai_tools_other: ai_tools_other.trim() }
                : {}),
              frustrations: frust.trim(),
              hopes: hp.trim(),
            },
          }
        : {}),
    };

    if (isValidtenant && isValidEmail) {
      AuthTracking.registerSubmitted("free");
      registerCompany(submitValues);
    }
  };

  const watchCompany = form.watch("company");
  const watchEmail = form.watch("email");

  const isCheckingTenant = isPendingTenantCheck || isTenantChecking;

  return (
    <BaseContainer
      footer={
        !isPending ? (
          <div>
            <span className="al-text-muted-foreground al-font-normal">
              Already have an account?{" "}
            </span>
            <Link to="https://app.myaltimate.com/login" className="al-text-primary">
              Sign In
            </Link>
            .
          </div>
        ) : null
      }
    >
      {isPending && (
        <Stack
          direction="column"
          align="center"
          className={cn({
            "al-w-[90vw] al-text-center": isMobileView,
          })}
        >
          <img
            src={query_insights_robot}
            alt="Loading"
            className={cn({
              "al-w-3/5": !isMobileView,
              "al-w-[90vw]": isMobileView,
            })}
          />
          <div className="al-text-black al-text-lg al-mt-4">
            Instance is being created, It will take about a minute
          </div>
        </Stack>
      )}
      {!isPending && (
        <div className={cn({ "al-w-[90vw]": isMobileView })}>
          {isExtensionVisitor && (
            <div className="al-p-4 al-bg-[#fece73] al-rounded-lg al-mb-8">
              If you want to know more details about Altimate AI, please visit the{" "}
              <Link
                to="https://www.altimate.ai"
                target="_blank"
                rel="noreferrer"
                className="al-underline"
              >
                website
              </Link>{" "}
              and{" "}
              <Link
                to="https://docs.myaltimate.com"
                target="_blank"
                rel="noreferrer"
                className="al-underline"
              >
                documentation
              </Link>
              .
            </div>
          )}
          <Typography variant="page-heading">Sign Up</Typography>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleFormSubmit)}
              className={cn("al-mt-4", { "al-w-full al-mt-3": isMobileView })}
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="al-mb-3">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Name"
                        {...field}
                        onFocus={onFocusChange}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="al-mb-3">
                    <FormLabel>Business Email</FormLabel>
                    <FormControl>
                      <Input
                        id={EMAIL_INPUT_ID}
                        type="email"
                        placeholder="name@company.com"
                        {...field}
                        value={field.value.toLowerCase()}
                        onFocus={onFocusChange}
                        onBlur={(e: FocusEvent<HTMLInputElement>) => {
                          field.onBlur();
                          const hasReferral = (form.getValues("referral_code")?.trim() ?? "") !== "";
                          const newEmailError = validateEmail(e.target.value.toLowerCase(), hasReferral);
                          if (newEmailError) {
                            form.setError("email", { message: newEmailError });
                          } else {
                            form.clearErrors("email");
                            const emailParts = e.target.value.split("@");
                            if (emailParts.length === 2) {
                              const domainParts = emailParts[1].split(".");
                              if (!form.getValues("company") && domainParts.length >= 2) {
                                form.setValue("company", domainParts[0].toLowerCase());
                                debouncedIsTenantAvailable(domainParts[0].toLowerCase());
                              }
                            }
                          }
                        }}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="referral_code"
                render={({ field }) => (
                  <FormItem className="al-mb-3">
                    <FormLabel htmlFor={REFERRAL_CODE_INPUT_ID}>
                      Referral code{" "}
                      <span className="al-text-muted-foreground al-font-normal">(Optional)</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        id={REFERRAL_CODE_INPUT_ID}
                        placeholder="e.g. WELCOME2026"
                        {...field}
                        onBlur={(e: FocusEvent<HTMLInputElement>) => {
                          field.onBlur();
                          const value = e.target.value.trim();
                          if (value !== "" && !REFERRAL_CODE_PATTERN.test(value)) {
                            form.setError("referral_code", {
                              message:
                                "Use 4–64 letters, numbers, hyphens or underscores",
                            });
                          } else {
                            form.clearErrors("referral_code");
                          }
                          const email = form.getValues("email");
                          if (email) {
                            const emailError = validateEmail(email.toLowerCase(), value !== "");
                            if (emailError) {
                              form.setError("email", { message: emailError });
                            } else {
                              form.clearErrors("email");
                            }
                          }
                        }}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("referral_code")?.trim() !== "" && (
                <Card className="al-bg-[rgba(17,30,49,0.04)] al-rounded-xl al-border-0 al-p-0 al-mb-3">
                  <CardHeader className="al-p-0 al-mb-2">
                    <Typography variant="body" weight="medium">
                      Tell us a bit about yourself
                    </Typography>
                    <div className="al-text-muted-foreground al-font-normal al-mb-2 al-mt-1 al-text-sm">
                      Required when using a referral code.
                    </div>
                  </CardHeader>
                  <CardContent className="al-p-0">
                    <Stack direction="column" gap="md">
                      <div>
                        <Label className="al-mb-2 al-block">
                          Which AI tools have you used?{" "}
                          <span className="al-text-destructive">*</span>
                        </Label>
                        <Stack direction="column" gap="xs">
                          {AI_TOOL_OPTIONS.map(opt => {
                            const currentTools = form.watch("ai_tools");
                            const checked = currentTools.includes(opt.id);
                            return (
                              <Stack key={opt.id} direction="row" align="center" gap="sm">
                                <Checkbox
                                  id={`ai-tool-${opt.id}`}
                                  checked={checked}
                                  onCheckedChange={next => {
                                    const prev = form.getValues("ai_tools");
                                    form.setValue(
                                      "ai_tools",
                                      next ? [...prev, opt.id] : prev.filter(v => v !== opt.id)
                                    );
                                  }}
                                  disabled={form.formState.isSubmitting}
                                />
                                <Label
                                  htmlFor={`ai-tool-${opt.id}`}
                                  className="al-font-normal al-cursor-pointer"
                                >
                                  {opt.label}
                                </Label>
                              </Stack>
                            );
                          })}
                          <Stack direction="row" align="center" gap="sm">
                            <Checkbox
                              id="ai-tool-other"
                              checked={form.watch("ai_tools").includes("other")}
                              onCheckedChange={next => {
                                const prev = form.getValues("ai_tools");
                                form.setValue(
                                  "ai_tools",
                                  next ? [...prev, "other"] : prev.filter(v => v !== "other")
                                );
                                if (!next) form.setValue("ai_tools_other", "");
                              }}
                              disabled={form.formState.isSubmitting}
                            />
                            <Label
                              htmlFor="ai-tool-other"
                              className="al-font-normal al-cursor-pointer"
                            >
                              Other
                            </Label>
                          </Stack>
                          {form.watch("ai_tools").includes("other") && (
                            <FormField
                              control={form.control}
                              name="ai_tools_other"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      placeholder="Which one(s)?"
                                      {...field}
                                      disabled={form.formState.isSubmitting}
                                      aria-label="Other AI tools"
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          )}
                        </Stack>
                      </div>

                      <FormField
                        control={form.control}
                        name="frustrations"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="survey-frustrations">
                              What&apos;s been frustrating about them?{" "}
                              <span className="al-text-destructive">*</span>
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                id="survey-frustrations"
                                {...field}
                                disabled={form.formState.isSubmitting}
                                rows={3}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="hopes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel htmlFor="survey-hopes">
                              What are you hoping to get from Altimate?{" "}
                              <span className="al-text-destructive">*</span>
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                id="survey-hopes"
                                {...field}
                                disabled={form.formState.isSubmitting}
                                rows={3}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              )}

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="al-mb-3">
                    <FormLabel htmlFor={PASSWORD_INPUT_ID}>Password</FormLabel>
                    <FormControl>
                      <div className="al-relative">
                        <InputPassword
                          id={PASSWORD_INPUT_ID}
                          placeholder="Password"
                          {...field}
                          onFocus={onFocusChange}
                          onBlur={() => {
                            field.onBlur();
                            setValidatePassword(prev =>
                              prev.map(element => (element === 0 ? -1 : element))
                            );
                          }}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            field.onChange(e);
                            validateFormPassword(e.target.value, setValidatePassword);
                          }}
                          disabled={form.formState.isSubmitting}
                          className="al-pr-10"
                        />
                      </div>
                    </FormControl>
                    {(activeElement?.id === PASSWORD_INPUT_ID || validatePassword.includes(-1)) && (
                      <PasswordValidation validatePassword={validatePassword} />
                    )}
                  </FormItem>
                )}
              />

              <Card className="al-bg-[rgba(17,30,49,0.04)] al-rounded-xl al-border-0 al-items-start al-self-stretch al-p-0 al-mb-3">
                <CardHeader className="al-p-0 al-mb-2">
                  <Label htmlFor={TENANT_INPUT_ID}>Instance Name</Label>
                  <div className="al-text-muted-foreground al-font-normal al-mb-2 al-mt-1 al-text-sm">
                    This is the name of the Altimate AI instance for your company.
                  </div>
                </CardHeader>
                <CardContent className="al-p-0">
                  <Stack direction="column" gap="sm">
                    <FormField
                      control={form.control}
                      name="company"
                      render={({ field }) => (
                        <FormItem>
                          <div className="al-relative">
                            <FormControl>
                              <Input
                                id={TENANT_INPUT_ID}
                                type="text"
                                placeholder="company"
                                {...field}
                                onFocus={onFocusChange}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                  const val = e.target.value;
                                  const lowerCaseNumbersOnly = /^[a-z0-9]+$/;
                                  // If email is entered, but instance name is deleted, show error
                                  if (!val && watchEmail) {
                                    field.onChange(e);
                                    debouncedFnRef.current.cancel();
                                    setIsPendingTenantCheck(false);
                                    form.setError("company", {
                                      message: "Please enter a valid instance name",
                                    });
                                    setIsValidTenant(false);
                                    return;
                                  }
                                  if (val === "" || lowerCaseNumbersOnly.test(val)) {
                                    field.onChange(e);
                                    debouncedIsTenantAvailable(val);
                                  }
                                }}
                                disabled={form.formState.isSubmitting}
                              />
                            </FormControl>
                            <div className="al-absolute al-top-3 al-right-2">
                              <Tooltip title="Only use lowercase letters & numbers, without any special characters">
                                <IconInfoCircle className="al-size-4 al-text-muted-foreground" />
                              </Tooltip>
                            </div>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {watchCompany && (
                      <Stack
                        direction="row"
                        justify="between"
                        align="center"
                        className="al-w-full al-mt-2"
                      >
                        <Stack
                          direction="row"
                          gap="none"
                          align="end"
                          className={cn({
                            "al-text-destructive": !validTenant && !isCheckingTenant,
                          })}
                        >
                          <div
                            className={cn({
                              "al-text-muted-foreground": validTenant || isCheckingTenant,
                            })}
                          >
                            https://
                          </div>
                          <div>{watchCompany.toLowerCase() || ""}</div>
                          <div
                            className={cn({
                              "al-text-muted-foreground": validTenant || isCheckingTenant,
                            })}
                          >
                            {"." + FREE_USER_DOMAIN}
                          </div>
                        </Stack>
                        <div className="al-size-4">
                          {isCheckingTenant ? (
                            <Spinner size="sm" />
                          ) : validTenant ? (
                            <IconCircleCheck className="al-size-4 al-text-green-500" />
                          ) : (
                            <IconAlertCircle className="al-size-4 al-text-destructive" />
                          )}
                        </div>
                      </Stack>
                    )}
                    {!validTenant && !isCheckingTenant && tenantError !== "" && (
                      <span
                        className="al-mt-2 al-text-sm al-text-destructive"
                        dangerouslySetInnerHTML={{ __html: tenantError }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <div className="al-mb-3">
                <Label htmlFor="signup-source" className="al-mb-2 al-block">
                  How did you hear about us{" "}
                  <span className="al-text-muted-foreground">(Optional)</span>
                </Label>
                <Combobox
                  id="signup-source"
                  value={form.watch("source_insight")}
                  options={SIGNUP_SOURCE_OPTIONS}
                  onChange={onChangeSelectSource}
                  placeholder="Select an option"
                  buttonProps={{ variant: "outline", className: "al-w-full al-justify-between" }}
                />
              </div>

              {form.watch("source_insight") === "Other" && (
                <FormField
                  control={form.control}
                  name="other_source"
                  render={({ field }) => (
                    <FormItem className="al-mb-3">
                      <FormLabel htmlFor={OTHER_SOURCE_INPUT_ID}>
                        Please specify <span className="al-text-destructive">*</span>
                      </FormLabel>
                      <FormControl>
                        <Input
                          id={OTHER_SOURCE_INPUT_ID}
                          type="text"
                          placeholder="How did you hear about us?"
                          {...field}
                          disabled={form.formState.isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="newsletter"
                render={({ field }) => (
                  <FormItem className="al-mb-4">
                    <div className="al-mb-1">
                      <Stack align="center">
                        <FormControl>
                          <Checkbox
                            id="newsletter"
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={form.formState.isSubmitting}
                          />
                        </FormControl>
                        <FormLabel
                          className="al-text-muted-foreground al-font-normal"
                          htmlFor="newsletter"
                        >
                          Subscribe to our newsletter & stay updated
                        </FormLabel>
                      </Stack>
                    </div>
                    <p className="al-mb-0 al-text-xs al-text-muted-foreground">
                      <span>By creating an account, you agree to the </span>
                      <Link
                        to="https://www.altimate.ai/terms"
                        target="_blank"
                        className="al-text-foreground al-font-medium"
                      >
                        Terms & Conditions
                      </Link>
                      <span> and our </span>
                      <Link
                        to="https://www.altimate.ai/privacy"
                        target="_blank"
                        className="al-text-foreground al-font-medium"
                      >
                        Privacy Policy
                      </Link>
                    </p>
                  </FormItem>
                )}
              />

              <div className="al-mt-4">
                <Button type="submit" className="al-w-full" disabled={isFormDisabled()}>
                  Sign Up
                </Button>
              </div>
            </form>
          </Form>
        </div>
      )}
    </BaseContainer>
  );
};

export { FreeRegister };
